import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FadeUp } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Eyebrow = ({ children, dark = false, className = "" }) => (
  <p className={`flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.35em] ${dark ? "text-champagne" : "text-champagne"} ${className}`}>
    <span className="inline-block h-px w-10 bg-champagne" aria-hidden="true" />
    {children}
  </p>
);

const btnBase =
  "group relative inline-flex items-center justify-center gap-3 overflow-hidden px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] transition-colors duration-500";

export const LuxeButton = ({ to, href, onClick, variant = "dark", children, testId, className = "", type }) => {
  const styles = {
    dark: "bg-charcoal text-ivory hover:bg-champagne hover:text-obsidian",
    light: "bg-ivory text-charcoal hover:bg-champagne hover:text-obsidian",
    "outline-light": "border border-ivory/60 text-ivory hover:border-champagne hover:text-champagne",
    "outline-dark": "border border-charcoal/40 text-charcoal hover:border-champagne hover:text-champagne",
    gold: "bg-champagne text-obsidian hover:bg-ivory hover:text-charcoal",
  };
  const cls = `${btnBase} ${styles[variant]} ${className}`;
  if (to)
    return (
      <Link to={to} className={cls} data-testid={testId} onClick={onClick}>
        {children}
      </Link>
    );
  if (href)
    return (
      <a href={href} className={cls} data-testid={testId} onClick={onClick}>
        {children}
      </a>
    );
  return (
    <button type={type || "button"} onClick={onClick} className={cls} data-testid={testId}>
      {children}
    </button>
  );
};

export const ChapterHeading = ({ number, eyebrow, title, dark = false, className = "" }) => (
  <FadeUp className={className}>
    <div className="flex items-baseline gap-5">
      {number && (
        <span className={`font-serif text-5xl font-light italic sm:text-6xl ${dark ? "text-ivory/20" : "text-charcoal/15"}`} aria-hidden="true">
          {number}
        </span>
      )}
      <div>
        {eyebrow && <Eyebrow dark={dark} className="mb-3">{eyebrow}</Eyebrow>}
        <h2 className={`font-serif text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl lg:text-5xl ${dark ? "text-ivory" : "text-charcoal"}`}>
          {title}
        </h2>
      </div>
    </div>
  </FadeUp>
);

export const Field = ({ label, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-muted">
      {label}
    </label>
    {children}
  </div>
);

export const inputCls =
  "w-full border border-warm-border bg-white px-4 py-3.5 text-sm text-charcoal placeholder:text-charcoal/30 transition-colors duration-300 focus:border-champagne focus:outline-none";

export const SmsConsent = ({ checked, onChange, id }) => (
  <div className="border border-warm-border bg-stone-beige/60 p-5">
    <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-muted">Additional Info</p>
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#C9A961]"
        data-testid={`${id}-checkbox`}
      />
      <span className="text-xs leading-relaxed text-slate-muted">
        I agree to receive marketing and promotional messages from Soby &amp; Associates at the phone number provided above. This agreement is not a
        condition of any purchase. Message and data rates may apply, and message frequency may vary. Text HELP to +1 (240) 222-5059 for assistance,
        or reply STOP or OUT to opt out or unsubscribe at any time.
      </span>
    </label>
  </div>
);

export const RecaptchaNotice = () => (
  <p className="text-[11px] leading-relaxed text-slate-muted/80">
    This site is protected by reCAPTCHA. Google&apos;s{" "}
    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-charcoal">
      Privacy Policy
    </a>{" "}
    and{" "}
    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-charcoal">
      Terms of Service
    </a>{" "}
    apply.
  </p>
);

export const useLeadSubmit = (formType) => {
  const [submitting, setSubmitting] = useState(false);
  const submit = async (payload) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { form_type: formType, ...payload });
      toast.success("Thank you — your message has been received.", {
        description: "A member of our team will be in touch shortly.",
      });
      return true;
    } catch (e) {
      toast.error("Something went wrong.", {
        description: "Please try again, or call us directly at 240-372-4804.",
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  };
  return { submit, submitting };
};
